import { useCallback, useState } from "react";
import { toast } from "react-toastify";

/**
 * A message that is also a toast.
 *
 * The maintenance console kept every error in a banner at the top of the page.
 * On a long screen that is the same as hiding it: the campaign builder's
 * override button sits hundreds of lines below its own error banner, so a
 * failed click scrolled its only feedback off-screen and the button read as
 * broken. react-toastify was already mounted in App.tsx and already used by the
 * account and members screens; maintenance simply never adopted it.
 *
 * Drop-in for useState: same tuple, same call sites. The banner still renders
 * where a component wants one, and the toast guarantees it is seen.
 *
 * Errors linger longer than successes because they usually need reading, and
 * the message doubles as the toast id so a repeated failure -- a poll that
 * keeps failing, a double click -- updates one toast instead of stacking a
 * tower of identical ones.
 */
type Kind = "error" | "success";

/* Overloaded rather than generic on purpose.
 *
 * The first version was `useNotice<T extends string | null>(kind, initial: T)`.
 * When a type parameter is constrained to a union of primitives, TypeScript
 * infers the LITERAL type of the argument -- so `useNotice("error", "")` gave
 * T = "" and a setter that accepted only the empty string. Every real message
 * was then a type error, 140 of them across the console. Two overloads pin the
 * widened type instead, and preserve the string | null variants that some
 * screens already used. */
export function useNotice(kind: Kind, initial: null): [string | null, (m: string | null) => void];
export function useNotice(kind: Kind, initial: string): [string, (m: string) => void];
// The implementation signature is not part of the public contract -- the two
// overloads above are -- and it has to be assignable to BOTH of them, which a
// concrete tuple cannot be (string | null is not string). This is the standard
// escape for an overload implementation; callers never see it.
export function useNotice(kind: Kind, initial: string | null): [any, any] {
  const [value, setValue] = useState<string | null>(initial);

  const publish = useCallback(
    (m: string | null) => {
      setValue(m);
      const text = (m ?? "").toString().trim();
      if (!text) return; // clearing is not an event worth announcing
      const opts = { toastId: `${kind}:${text}`, autoClose: kind === "error" ? 6000 : 3000 };
      if (kind === "error") toast.error(text, opts);
      else toast.success(text, opts);
    },
    [kind]
  );

  return [value, publish];
}
